import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Clapperboard, Loader2, LogOut, Package, Clock, DollarSign, CheckCircle2,
  Send, X, ExternalLink, FileText, Mail, Phone, Download, Plus, Paperclip,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL, TOKEN_KEY } from "@/lib/api";

const STATUS_META = {
  in_queue: { label: "In queue", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  in_progress: { label: "In production", cls: "bg-blue-500/15 text-blue-400 border-blue-500/30" },
  delivered: { label: "Delivered", cls: "bg-[#00F5D4]/15 text-[#00F5D4] border-[#00F5D4]/30" },
  awaiting_payment: { label: "Unpaid", cls: "bg-slate-500/15 text-slate-400 border-slate-500/30" },
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) {
      api.get("/auth/me").then(() => setAuthed(true)).catch(() => {
        localStorage.removeItem(TOKEN_KEY); setAuthed(false);
      }).finally(() => setChecking(false));
    }
  }, []);

  if (checking) return <div className="min-h-screen grid place-items-center bg-[#0D0F17]"><Loader2 className="w-7 h-7 animate-spin text-[#FF2E63]" /></div>;
  return authed ? <Dashboard onLogout={() => { localStorage.removeItem(TOKEN_KEY); setAuthed(false); }} /> : <LoginGate onLogin={() => setAuthed(true)} />;
}

function LoginGate({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#0D0F17] px-4 itm-grid-bg">
      <div className="w-full max-w-sm" data-testid="admin-login-modal">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#FF2E63]"><Clapperboard className="w-5 h-5 text-white" /></span>
          <span className="font-display font-extrabold text-lg">iTMunch Admin</span>
        </Link>
        <form onSubmit={submit} className="rounded-2xl bg-[#161924] border border-white/10 p-7 space-y-4">
          <h1 className="font-display text-xl font-bold">Sign in</h1>
          {error && <p className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2" data-testid="admin-login-error">{error}</p>}
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Email</label>
            <input data-testid="admin-email-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
              className="mt-2 w-full rounded-lg bg-[#0D0F17] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF2E63]/60" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Password</label>
            <input data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
              className="mt-2 w-full rounded-lg bg-[#0D0F17] border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:border-[#FF2E63]/60" />
          </div>
          <button data-testid="admin-login-submit-button" disabled={loading}
            className="w-full rounded-full bg-[#FF2E63] hover:bg-[#E02554] disabled:opacity-60 text-white font-semibold py-2.5 transition-colors inline-flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/orders", { params: filter !== "all" ? { status: filter } : {} }),
      ]);
      setStats(s.data); setOrders(o.data);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const statCards = [
    { label: "Total orders", value: stats?.total_orders ?? "—", icon: Package, color: "text-[#FF2E63]" },
    { label: "Pending queue", value: stats?.pending ?? "—", icon: Clock, color: "text-amber-400" },
    { label: "Delivered", value: stats?.delivered ?? "—", icon: CheckCircle2, color: "text-[#00F5D4]" },
    { label: "Revenue", value: stats ? `$${stats.revenue}` : "—", icon: DollarSign, color: "text-[#00F5D4]" },
  ];

  return (
    <div className="min-h-screen bg-[#0D0F17] text-[#F8F9FA]">
      <header className="itm-glass border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-[#FF2E63]"><Clapperboard className="w-4 h-4 text-white" /></span>
            <span className="font-display font-bold">iTMunch <span className="text-slate-500 font-normal text-sm">Admin</span></span>
          </Link>
          <button onClick={onLogout} data-testid="admin-logout-button" className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8" data-testid="admin-dashboard-container">
        <h1 className="font-display text-2xl font-bold">Orders dashboard</h1>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((c) => (
            <div key={c.label} className="rounded-xl bg-[#161924] border border-white/10 p-5">
              <c.icon className={`w-5 h-5 ${c.color}`} />
              <p className="mt-3 font-mono text-2xl font-bold">{c.value}</p>
              <p className="text-sm text-slate-400">{c.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="admin-order-status-filter">
          {["all", "in_queue", "in_progress", "delivered"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
                filter === f ? "bg-[#FF2E63] text-white" : "bg-[#161924] text-slate-400 hover:text-white border border-white/10"
              }`}>
              {f === "all" ? "All" : STATUS_META[f].label}
            </button>
          ))}
        </div>

        <div className="mt-5 rounded-xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF2E63]" /></div>
          ) : orders.length === 0 ? (
            <div className="py-16 text-center text-slate-500">No orders yet.</div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-orders-table">
              <thead className="bg-[#161924] text-slate-400 text-xs uppercase font-mono tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3">Order</th>
                  <th className="text-left px-5 py-3 hidden md:table-cell">Customer</th>
                  <th className="text-left px-5 py-3 hidden lg:table-cell">Package</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-right px-5 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const m = STATUS_META[o.status] || STATUS_META.in_queue;
                  return (
                    <tr key={o.id} className="border-t border-white/5 hover:bg-[#161924]/60 transition-colors">
                      <td className="px-5 py-4 font-mono">#{o.id.slice(0, 8).toUpperCase()}</td>
                      <td className="px-5 py-4 hidden md:table-cell">{o.name}<br /><span className="text-slate-500 text-xs">{o.email}</span></td>
                      <td className="px-5 py-4 hidden lg:table-cell">{o.tier_name} · ${o.amount}
                        {o.files?.length > 0 && (
                          <span data-testid={`admin-order-assets-count-${o.id.slice(0,8)}`} className="ml-2 inline-flex items-center gap-1 text-xs text-[#00F5D4]">
                            <Paperclip className="w-3 h-3" />{o.files.length}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4"><span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs ${m.cls}`}>{m.label}</span></td>
                      <td className="px-5 py-4 text-right">
                        <button onClick={() => setActive(o)} data-testid={`admin-view-order-${o.id.slice(0,8)}`}
                          className="rounded-full bg-[#FF2E63] hover:bg-[#E02554] text-white text-xs font-semibold px-4 py-1.5 transition-colors">
                          Manage
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {active && <OrderDrawer order={active} onClose={() => setActive(null)} onChanged={() => { load(); setActive(null); }} />}
    </div>
  );
}

function BrandAssets({ files, token }) {
  const images = files.filter((f) => (f.content_type || "").startsWith("image/"));
  const others = files.filter((f) => !(f.content_type || "").startsWith("image/"));
  const url = (f) => `${BACKEND_URL}/api/files/${f.path}?auth=${token}`;
  return (
    <div data-testid="admin-brand-assets">
      <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Logo & brand assets ({files.length})</p>
      {files.length === 0 && (
        <p data-testid="admin-brand-assets-empty" className="text-sm text-slate-500 bg-[#0D0F17] rounded-lg p-3 border border-white/10">Customer didn't upload any files.</p>
      )}
      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-2">
          {images.map((f) => (
            <a key={f.id} href={url(f)} target="_blank" rel="noreferrer" data-testid={`admin-asset-image-${f.id.slice(0,8)}`}
              className="group rounded-lg border border-white/10 bg-[#0D0F17] overflow-hidden hover:border-[#FF2E63]/40 transition-colors">
              <div className="aspect-square grid place-items-center bg-[repeating-conic-gradient(#1c1f2b_0%_25%,#13151e_0%_50%)] bg-[length:16px_16px]">
                <img src={url(f)} alt={f.filename} className="max-h-full max-w-full object-contain p-2" />
              </div>
              <div className="flex items-center gap-1 px-2 py-1.5 text-xs text-slate-400">
                <span className="flex-1 truncate">{f.filename}</span><Download className="w-3.5 h-3.5 group-hover:text-white" />
              </div>
            </a>
          ))}
        </div>
      )}
      <div className="space-y-2">
        {others.map((f) => (
          <a key={f.id} href={url(f)} target="_blank" rel="noreferrer" data-testid={`admin-asset-file-${f.id.slice(0,8)}`}
            className="flex items-center gap-2 text-sm text-slate-300 bg-[#0D0F17] rounded-lg px-3 py-2 border border-white/10 hover:border-[#FF2E63]/40">
            <FileText className="w-4 h-4 text-[#00F5D4]" /> <span className="flex-1 truncate">{f.filename}</span> <Download className="w-4 h-4 text-slate-500" />
          </a>
        ))}
      </div>
    </div>
  );
}

function OrderDrawer({ order, onClose, onChanged }) {
  const [videoUrl, setVideoUrl] = useState(order.video_url || "");
  const [sending, setSending] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const token = localStorage.getItem(TOKEN_KEY);

  const deliver = async () => {
    if (!videoUrl.trim()) { toast.error("Enter a video link first"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/deliver`, { video_url: videoUrl.trim() });
      toast.success(data.email_sent ? "Video delivered & email sent to customer!" : "Video saved. Email could not be sent (check credits).");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to deliver");
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    setStatusBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/status`, { status });
      toast.success(data.email_sent ? "Status updated — customer notified by email" : "Status updated");
      onChanged();
    } catch {
      toast.error("Failed to update status");
    } finally {
      setStatusBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end" data-testid="admin-send-video-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md h-full bg-[#161924] border-l border-white/10 overflow-y-auto">
        <div className="sticky top-0 itm-glass border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <h3 className="font-display font-bold">Order #{order.id.slice(0, 8).toUpperCase()}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-2 text-sm">
            <Row icon={Mail} label="Email" value={order.email} />
            <Row icon={Phone} label="Phone" value={order.phone || "—"} />
            <Row icon={Package} label="Package" value={`${order.tier_name} · ${order.videos} video(s) · $${order.amount}`} />
            {order.extra_format && <Row icon={Plus} label="Add-on" value={`Extra format (+$${order.extra_format_fee || 10})${order.extra_format_note ? `: ${order.extra_format_note}` : ""}`} />}
            <Row icon={ExternalLink} label="Product URL" value={<a href={order.product_url} target="_blank" rel="noreferrer" className="text-[#00F5D4] break-all">{order.product_url}</a>} />
          </div>

          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Brand guidelines</p>
            <p className="text-sm text-slate-300 bg-[#0D0F17] rounded-lg p-3 border border-white/10 whitespace-pre-wrap">{order.brand_guidelines || "—"}</p>
          </div>

          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Comments & expectations</p>
            <p data-testid="admin-order-comments" className="text-sm text-slate-300 bg-[#0D0F17] rounded-lg p-3 border border-white/10 whitespace-pre-wrap">{order.comments || "—"}</p>
          </div>

          <div data-testid="admin-order-reference-links">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Reference videos ({order.reference_links?.length || 0})</p>
            {order.reference_links?.length ? (
              <div className="space-y-2">
                {order.reference_links.map((u, i) => (
                  <a key={i} href={u} target="_blank" rel="noreferrer" data-testid={`admin-reference-link-${i}`}
                    className="flex items-center gap-2 text-sm text-[#00F5D4] bg-[#0D0F17] rounded-lg px-3 py-2 border border-white/10 hover:border-[#FF2E63]/40 break-all">
                    <ExternalLink className="w-4 h-4 shrink-0" /> <span className="flex-1">{u}</span>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 bg-[#0D0F17] rounded-lg p-3 border border-white/10">—</p>
            )}
          </div>

          <BrandAssets files={order.files || []} token={token} />

          <div className="flex gap-2">
            {["in_queue", "in_progress"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} disabled={statusBusy}
                className={`flex-1 rounded-lg border text-xs py-2 transition-colors ${order.status === s ? "border-[#FF2E63] bg-[#FF2E63]/10 text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>
                {STATUS_META[s].label}
              </button>
            ))}
          </div>

          <div className="rounded-xl bg-[#0D0F17] border border-[#00F5D4]/30 p-4">
            <p className="text-xs font-mono uppercase tracking-widest text-[#00F5D4] mb-2">Deliver video</p>
            <p className="text-xs text-slate-400 mb-3">Paste the final video link. The customer gets an email with a link to their order page to watch & download.</p>
            <input data-testid="admin-video-url-input" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://drive.google.com/…"
              className="w-full rounded-lg bg-[#161924] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#00F5D4]/60" />
            <button data-testid="admin-send-video-submit-button" onClick={deliver} disabled={sending}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#00F5D4] hover:bg-[#00d9bd] disabled:opacity-60 text-[#0A0B10] font-bold py-2.5 transition-colors">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Deliver & notify customer</>}
            </button>
            {order.status === "delivered" && <p className="mt-2 text-xs text-[#00F5D4]">✓ Already delivered</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
    <span className="text-slate-500 w-20 shrink-0">{label}</span>
    <span className="text-slate-200 flex-1 break-words">{value}</span>
  </div>
);
